import React from 'react';
import { Button, Tooltip } from 'antd';
import { EyeOutlined, EditOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import type { ColumnsType } from 'antd/es/table';
import { truncateAddress } from '@/utils/address';
import { CopyIcon } from '@/components/DataTable';
import { CommitmentDetail } from '@/api/commitment';
import { TransactionBaseDto } from '@/types/commitment';
import AssetSymbol from '@/components/AssetView/AssetSymbol';
import { Hash } from 'viem';
import { SupportedChainId } from '@packet/constants/AppSupportChains';
import AssetAmount from '@/components/AssetView/AssetAmount';
import { CommitmentStatus } from '@/components/CommitmentStatus';
import { timeFmt } from '@/utils/timeFmt';

// 获取UTXO表格列定义
export const getUTXOColumns = (
  chainId: SupportedChainId,
  onViewCommitment: (record: CommitmentDetail) => void,
  onViewTransaction: (record: TransactionBaseDto) => void,
): ColumnsType<CommitmentDetail> => [
  {
    title: 'UTXO ID',
    dataIndex: 'commitment',
    key: 'commitment',
    align: 'left',
    render: (id: string, record: CommitmentDetail) => (
      <HashContainer>
        <HashText
          onClick={(e) => {
            e.stopPropagation();
            onViewCommitment(record);
          }}
        >
          {truncateAddress(id, 8, 6)}
        </HashText>
        <CopyIcon className="copy-icon" text={id} />
      </HashContainer>
    ),
  },
  {
    title: 'Transactions Hash',
    dataIndex: ['from'],
    key: 'txhash',
    align: 'left',
    render: (from: Array<TransactionBaseDto>) => (
      <HashContainer>
        <HashText
          onClick={(e) => {
            e.stopPropagation();
            onViewTransaction(from[0]);
          }}
          style={{ color: '#388506' }}
        >
          {truncateAddress(`${from[0]?.txhash}-${from[0]?.tx_index}`, 8, 6)}
        </HashText>
        <CopyIcon className="copy-icon" text={`${from[0]?.txhash}-${from[0]?.tx_index}`} />
      </HashContainer>
    ),
  },
  {
    title: 'Token',
    dataIndex: 'asset',
    key: 'asset',
    align: 'left',
    render: (asset: Hash) => <AssetSymbol assetId={asset} chainId={chainId} />,
  },
  {
    title: 'Amount',
    dataIndex: 'amount',
    key: 'amount',
    align: 'left',
    render: (amount: string, record: CommitmentDetail) => <AssetAmount assetId={record.asset} chainId={chainId} amount={amount} displayDigits={6} />,
  },
  {
    title: 'State',
    dataIndex: 'is_frozen',
    key: 'state',
    align: 'left',
    render: (is_frozen: boolean, record: CommitmentDetail) => (
      <div style={{ textAlign: 'left' }}>
        <CommitmentStatus is_frozen={is_frozen} is_spent={record.is_spent} />
      </div>
    ),
  },
  {
    title: 'Time',
    dataIndex: 'created_at',
    key: 'created_at',
    align: 'left',
    render: (timestamp: number) => <TimeText>{timeFmt(timestamp)}</TimeText>,
  },
  {
    title: 'Operate',
    key: 'operate',
    render: (_, record: CommitmentDetail) => (
      <OperateContainer>
        <OperateButton onClick={() => onViewCommitment(record)}>
          <EyeOutlined />
        </OperateButton>
      </OperateContainer>
    ),
  },
];

// Styled Components
const HashContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  .copy-icon {
    opacity: 0;
    pointer-events: none;
    transition: all ease 0.2s;
  }
  &:hover {
    .copy-icon {
      opacity: 1;
      pointer-events: inherit;
    }
  }
`;

const HashText = styled.span`
  font-family: 'Poppins', sans-serif;
  font-size: 16px;
  color: #141413;
  cursor: pointer;
`;

const TimeText = styled.span`
  font-family: 'Poppins', sans-serif;
  font-size: 14px;
  color: rgba(16, 41, 0, 0.7);
`;

const OperateContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  padding-right: 0;
`;

const OperateButton = styled.div`
  width: 36px;
  height: 36px;
  background: #f5f5f5;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;

  &:hover {
    background: rgba(243, 245, 242, 0.8);
  }
`;

const FreezeButton = styled(OperateButton)`
  background: rgba(248, 152, 61, 0.12);

  &:hover {
    background: rgba(248, 152, 61, 0.2);
  }
`;

const UnfreezeButton = styled(OperateButton)`
  background: #a6eb7a;

  &:hover {
    background: rgba(166, 235, 122, 0.8);
  }
`;

const LockIcon = styled.div`
  width: 20px;
  height: 20px;

  &::before {
    content: '🔒';
    font-size: 16px;
    color: #f8983d;
  }
`;

const UnlockIcon = styled.div`
  width: 20px;
  height: 20px;

  &::before {
    content: '🔓';
    font-size: 16px;
    color: #141413;
  }
`;
